<mat-card class="rounded-3xl border border-slate-200 p-4 shadow-lg shadow-slate-200/60">
  <div class="mb-3 flex items-center justify-between gap-3">
    <h3 class="text-lg font-semibold text-slate-900">Reports</h3>
    @if (canSubmit() && !formOpen()) {
      <button mat-flat-button type="button" (click)="open()">Submit report</button>
    }
  </div>

  @if (formOpen()) {
    <form [formGroup]="form" (ngSubmit)="save()" class="mb-4 grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <h4 class="font-semibold text-slate-900">Submit a report</h4>

      <mat-form-field appearance="outline">
        <mat-label>Report type</mat-label>
        <mat-select formControlName="reportType">
          @for (item of reportTypes(); track item.type) {
            <mat-option [value]="item.type">{{ item.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      @if (selectedType() === 'CloseOut') {
        <div class="grid gap-1 rounded-2xl border border-slate-200 bg-white p-3 text-sm text-slate-700">
          <span><span class="font-medium">File number:</span> {{ intervention()?.interventionNumber }}</span>
          <span>
            <span class="font-medium">Dates of service:</span>
            @for (date of datesOfService(); track $index; let last = $last) {
              {{ date | date: 'yyyy-MM-dd' }}{{ last ? '' : ',' }}
            } @empty {
              none recorded
            }
          </span>
        </div>
        <mat-form-field appearance="outline">
          <mat-label>Case status *</mat-label>
          <mat-select formControlName="closureOutcomeId">
            @for (item of caseStatuses(); track item.id) {
              <mat-option [value]="item.id">{{ item.name ?? item.code }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      }

      @if (selectedType() === 'Session') {
        <mat-form-field appearance="outline">
          <mat-label>Session *</mat-label>
          <mat-select formControlName="sessionId">
            @for (session of sessions(); track session.id) {
              <mat-option [value]="session.id">
                Session {{ session.sessionNumber }} · {{ session.sessionDate | date: 'yyyy-MM-dd HH:mm' }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      }

      @if (selectedType() !== 'CloseOut') {
        <mat-form-field appearance="outline">
          <mat-label>Summary *</mat-label>
          <textarea matInput rows="4" maxlength="4000" formControlName="summary"></textarea>
        </mat-form-field>
      }

      @if (selectedType() === 'CloseOut' && goalOutcomes.length > 0) {
        <section class="grid gap-2">
          <h5 class="font-medium text-slate-900">Treatment goals and what was achieved</h5>
          @for (row of goalOutcomes.controls; track row; let i = $index) {
            <div class="grid gap-1 rounded-2xl border border-slate-200 bg-white p-3" [formGroup]="row">
              <span class="text-sm text-slate-700">{{ i + 1 }}. {{ row.controls.goal.value }}</span>
              <mat-form-field appearance="outline">
                <mat-label>Outcome achieved</mat-label>
                <textarea matInput rows="2" maxlength="2000" formControlName="outcome"></textarea>
              </mat-form-field>
            </div>
          }
        </section>
      }

      @if (selectedType() !== 'Session') {
        <mat-form-field appearance="outline">
          <mat-label>{{ selectedType() === 'CloseOut' && goalOutcomes.length > 0 ? 'Other goals achieved / outcomes' : 'Initial treatment goals achieved / outcomes' }}</mat-label>
          <textarea matInput rows="3" maxlength="4000" formControlName="goalsAchieved"></textarea>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Recommendations for unresolved issues</mat-label>
          <textarea matInput rows="3" maxlength="4000" formControlName="recommendations"></textarea>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Client's level of functioning at closure</mat-label>
          <input matInput maxlength="500" formControlName="functioningAtClosure" />
        </mat-form-field>
      }

      <div class="flex justify-end gap-2">
        <button mat-stroked-button type="button" (click)="closeForm()">Close</button>
        <button mat-flat-button type="submit" [disabled]="!canSave() || interventionStore.savingReport()">
          @if (interventionStore.savingReport()) {
            <mat-spinner diameter="20" class="mr-2" />
          }
          Submit report
        </button>
      </div>
    </form>
  }

  @if (interventionStore.reportsLoading() && interventionStore.reports().length === 0) {
    <div class="flex items-center gap-3 text-sm text-slate-600">
      <mat-spinner diameter="20" />
      Loading reports...
    </div>
  } @else if (interventionStore.reports().length === 0) {
    <p class="text-sm text-slate-500">No reports have been submitted yet.</p>
  } @else {
    <div class="grid gap-2">
      @for (report of interventionStore.reports(); track report.id) {
        <div class="grid gap-2 border-b border-slate-100 py-2 text-sm last:border-b-0">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="grid gap-0.5">
              <div class="flex items-center gap-2">
                <span class="font-medium text-slate-900">
                  {{ label(report.reportType) }}
                  @if (report.sessionNumber) {
                    · session {{ report.sessionNumber }}
                  }
                </span>
                <span
                  class="rounded-full px-2 py-0.5 text-xs font-medium"
                  [class.bg-blue-50]="report.status === 'Submitted'"
                  [class.text-blue-700]="report.status === 'Submitted'"
                  [class.bg-green-50]="report.status === 'Approved'"
                  [class.text-green-700]="report.status === 'Approved'"
                  [class.bg-amber-50]="report.status === 'Returned'"
                  [class.text-amber-700]="report.status === 'Returned'">
                  {{ report.status }}
                </span>
              </div>
              <span class="text-slate-600">
                Submitted {{ report.submittedDate | date: 'yyyy-MM-dd HH:mm' }} · {{ report.affiliateName ?? 'Internal' }}
              </span>
              @if (report.closureOutcomeName) {
                <span class="text-slate-700">Case status: {{ report.closureOutcomeName }}</span>
              }
              @if (report.summary) {
                <span class="text-slate-700">{{ report.summary }}</span>
              }
              @if (report.goalsAchieved) {
                <span class="text-slate-700"><span class="font-medium">Goals achieved:</span> {{ report.goalsAchieved }}</span>
              }
              @if (report.recommendations) {
                <span class="text-slate-700"><span class="font-medium">Recommendations:</span> {{ report.recommendations }}</span>
              }
              @if (report.functioningAtClosure) {
                <span class="text-slate-700"><span class="font-medium">Level of functioning:</span> {{ report.functioningAtClosure }}</span>
              }
              @if (report.reviewNotes) {
                <span class="text-amber-700">
                  {{ report.status === 'Returned' ? 'Returned' : 'Reviewer note' }}: {{ report.reviewNotes }}
                </span>
              }
            </div>

            @if (canReview() && report.status === 'Submitted' && reviewingId() !== report.id) {
              <button mat-stroked-button type="button" (click)="openReview(report)">Review</button>
            }
          </div>

          @if (reviewingId() === report.id) {
            <div class="flex flex-wrap items-start gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <mat-form-field appearance="outline" class="min-w-64 flex-1">
                <mat-label>Notes (required to return)</mat-label>
                <input matInput maxlength="1000" [formControl]="reviewNotes" />
              </mat-form-field>
              <button mat-stroked-button type="button" (click)="closeReview()">Close</button>
              <button
                mat-stroked-button
                type="button"
                [disabled]="!reviewNotes.value.trim() || interventionStore.savingReport()"
                (click)="returnReport(report)">
                Return
              </button>
              <button mat-flat-button type="button" [disabled]="interventionStore.savingReport()" (click)="approve(report)">
                Approve
              </button>
            </div>
          }
        </div>
      }
    </div>
  }
</mat-card>
